/**
 * Recap email payload — the one place the recap-to-email mapping lives.
 *
 * Family-visible tiers ONLY ('student' + 'parent'). The teacher tier is not an
 * argument here at all, so no caller can leak it into a parent's inbox — same
 * lock as a captured Song Note, enforced by shape rather than by discipline.
 */

/**
 * The sendLessonNotes template interpolates `notes` into HTML unescaped (it
 * only converts newlines to <br>), so escape here or a recap containing "<"
 * or "&" is swallowed as markup in the parent's mail client.
 */
const escapeForEmailHtml = (text) =>
	text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Build the sendLessonNotes payload, or null when there is nothing to send
 * (no recipient, or no family-visible content).
 *
 * @param {Object} params
 * @param {string} params.everyone   - Everyone-tier plain text
 * @param {string} params.parents    - Parents-tier plain text
 * @param {string} params.recipient  - parent email address
 * @param {string} params.studentName
 * @param {string} params.lessonDate - "YYYY-MM-DD" from the lesson doc, or ''
 */
export const buildRecapEmailPayload = ({
	everyone = '',
	parents = '',
	recipient = '',
	studentName = '',
	lessonDate = '',
}) => {
	const sections = [];
	if (everyone.trim()) sections.push(everyone.trim());
	if (parents.trim()) sections.push(`For the parents:\n${parents.trim()}`);
	if (!sections.length || !recipient) return null;
	return {
		studentName: studentName || 'your student',
		parentEmail: recipient,
		// `lesson.date` is "YYYY-MM-DD"; the callable runs it through
		// `new Date(...)`, which parses a bare date as UTC midnight — the evening
		// BEFORE in every US timezone. Noon keeps the rendered day right
		// whatever timezone renders it.
		lessonDate: lessonDate ? `${lessonDate}T12:00:00` : new Date().toISOString(),
		notes: escapeForEmailHtml(sections.join('\n\n')),
	};
};

export default buildRecapEmailPayload;
